import { toAudio } from "./timeline.js"

const TIMING = /^\s*((?:\d+:)?\d{1,2}:\d{2}[.,]\d{1,3})\s+-->\s+((?:\d+:)?\d{1,2}:\d{2}[.,]\d{1,3})/

export function timestampToSeconds(value) {
    const parts = value.trim().replace(",", ".").split(":")
    const seconds = parseFloat(parts.pop())
    const minutes = parseInt(parts.pop() ?? "0", 10)
    const hours = parseInt(parts.pop() ?? "0", 10)

    return hours * 3600 + minutes * 60 + seconds
}

const ENTITIES = { "&amp;": "&", "&lt;": "<", "&gt;": ">" }

// Tags first, entities second and in one pass, so an escaped "&lt;b&gt;"
// stays literal text instead of being decoded and then stripped.
function plainCueText(text) {
    return text
        .replace(/<[^>]*>/g, "")
        .replace(/&(?:amp|lt|gt);/g, (entity) => ENTITIES[entity])
        .trim()
}

// "<v Joost>" and "<v.loud Joost>": the annotation after the voice tag
// (and its optional classes) is the speaker's name.
const VOICE = /<v(?:\.[^\s>]*)?\s+([^>]*)>/

function cueSpeaker(text) {
    const name = plainCueText(VOICE.exec(text)?.[1] ?? "")

    return name === "" ? null : name
}

/**
 * WebVTT → [{start, end, text, speaker}] in seconds, as plain text: markup
 * tags (<v>, <c>, <i>, timestamps, ...) are stripped, &amp; &lt; &gt; decoded,
 * and multi-line cue text is joined with a space, matching the audio
 * player's transcript rendering. `speaker` is the first voice span's name
 * (AssemblyAI emits "<v Joost>…</v>") or null. Render with text bindings only.
 */
export function parseVTT(text) {
    if (typeof text !== "string") {
        return []
    }

    const lines = text.replace(/\r\n?/g, "\n").split("\n")
    const cues = []

    for (let i = 0; i < lines.length; i++) {
        const match = TIMING.exec(lines[i])

        if (!match) {
            continue
        }

        const textLines = []

        while (i + 1 < lines.length && lines[i + 1].trim() !== "" && !TIMING.test(lines[i + 1])) {
            i++
            textLines.push(lines[i].trim())
        }

        const raw = textLines.join(" ")

        cues.push({
            start: timestampToSeconds(match[1]),
            end: timestampToSeconds(match[2]),
            text: plainCueText(raw),
            speaker: cueSpeaker(raw),
        })
    }

    return cues
}

export function formatTimestamp(seconds) {
    const totalMs = Math.max(0, Math.round(seconds * 1000))
    const pad = (value, width = 2) => String(value).padStart(width, "0")
    const hours = Math.floor(totalMs / 3600000)
    const minutes = Math.floor((totalMs % 3600000) / 60000)
    const secs = Math.floor((totalMs % 60000) / 1000)

    return `${pad(hours)}:${pad(minutes)}:${pad(secs)}.${pad(totalMs % 1000, 3)}`
}

function cueText(text, escape) {
    const flat = String(text ?? "").replace(/\s*\n\s*/g, " ")

    return escape ? flat.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") : flat
}

/**
 * [{start, end, text}] → WebVTT. Escape cue text for chapters and subtitles;
 * pass {escape: false} for the thumbnails track, whose cue text is a URL.
 * `settings` (e.g. "line:-4") is appended to every cue's timing line.
 */
export function buildVtt(cues, { escape = true, settings = "" } = {}) {
    const suffix = settings ? ` ${settings}` : ""
    const blocks = cues.map((cue) => `${formatTimestamp(cue.start)} --> ${formatTimestamp(cue.end)}${suffix}\n${cueText(cue.text, escape)}`)

    return `${["WEBVTT", ...blocks].join("\n\n")}\n`
}

/**
 * Moves transcript cues (original timeline) onto the assembled-audio
 * timeline. A cue that an insertion starts inside of is cut off where the
 * insertion begins, so it never spans the inserted audio.
 */
export function shiftCues(cues, insertions) {
    if (!Array.isArray(insertions) || insertions.length === 0) {
        return cues
    }

    const positions = insertions.map((insertion) => insertion?.at_ms).filter(Number.isFinite).sort((a, b) => a - b)

    return cues.map((cue) => {
        const startMs = Math.round(cue.start * 1000)
        const endMs = Math.round(cue.end * 1000)
        const start = toAudio(cue.start, insertions)
        const cutAt = positions.find((at) => at > startMs && at <= endMs)
        const end = cutAt === undefined ? toAudio(cue.end, insertions) : start + (cutAt - startMs) / 1000

        return { ...cue, start, end }
    })
}

/**
 * Native captions render from the bottom of the video box, where the control
 * bar sits. A negative snap-to-lines position counts lines up from the bottom
 * and is honored by Chrome, Safari and Firefox alike: the last caption line
 * ends three line heights (~18% of the player height) above the bottom edge.
 */
export const SUBTITLE_CUE_SETTINGS = "line:-4"

/**
 * Browsers parse <v> voice spans but none of them shows the name, so the
 * speaker is written into the cue text instead: "Joost: …" on each cue where
 * the speaker changes from the previous one.
 */
function withSpeakerPrefixes(cues) {
    return cues.map((cue, index) => {
        const changed = cue.speaker && cue.speaker !== cues[index - 1]?.speaker

        return changed ? { ...cue, text: `${cue.speaker}: ${cue.text}` } : cue
    })
}

/**
 * Raw transcript text → a valid subtitles WebVTT. Pro transcripts can lack the
 * WEBVTT header, which browsers reject; rebuilding always emits one. Pass the
 * DC insertions to move the cues onto the assembled-audio timeline.
 */
export function subtitleVtt(text, insertions) {
    return buildVtt(shiftCues(withSpeakerPrefixes(parseVTT(text)), insertions), { settings: SUBTITLE_CUE_SETTINGS })
}
